/**
 * FilterSection Component
 * 라이더 선정산내역 검색 필터 영역
 */
import React, { useCallback, useMemo } from 'react';
import DateInput from '@/shared/components/forms/DateInput';
import SearchableSelect, { SelectOption } from '@/shared/components/forms/SearchableSelect';
import '@/shared/styles/search-form.css';
import '../styles/RiderPreSettlementHistory.css';

interface Supplier {
    id: string;
    name: string;
}

interface FilterSectionProps {
    filters: {
        acctStDate: string;
        acctEdDate: string;
        riderName: string;
        supplierId: string;
    };
    suppliers: Supplier[];
    isSupplierUser: boolean;
    onFilterChange: (key: string, value: string) => void;
    onSearch: () => void;
    onReset: () => void;
    onExcelDownload: () => void;
}

/** 날짜 포맷팅 (yyyy-MM-dd) */
const formatDate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/** 기간 프리셋 타입 */
type DatePreset = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

const FilterSection: React.FC<FilterSectionProps> = ({
    filters,
    suppliers,
    isSupplierUser,
    onFilterChange,
    onSearch,
    onReset,
    onExcelDownload
}) => {
    /**
     * 엔터키 입력 시 검색 실행
     */
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            onSearch();
        }
    };

    /**
     * 기간 프리셋 버튼 클릭 핸들러
     */
    const handlePresetClick = useCallback((preset: DatePreset) => {
        const today = new Date();
        let startDate: Date;
        let endDate: Date;

        switch (preset) {
            case 'thisWeek': {
                // 이번 주 (월요일 ~ 일요일)
                const dayOfWeek = today.getDay();
                const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                startDate = new Date(today);
                startDate.setDate(today.getDate() + diffToMonday);
                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + 6);
                break;
            }
            case 'lastWeek': {
                // 지난 주 (월요일 ~ 일요일)
                const dayOfWeek = today.getDay();
                const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
                startDate = new Date(today);
                startDate.setDate(today.getDate() + diffToMonday - 7);
                endDate = new Date(startDate);
                endDate.setDate(startDate.getDate() + 6);
                break;
            }
            case 'thisMonth': {
                // 이번 달 (1일 ~ 말일)
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                break;
            }
            case 'lastMonth': {
                // 지난 달 (1일 ~ 말일)
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            }
        }

        onFilterChange('acctStDate', formatDate(startDate));
        onFilterChange('acctEdDate', formatDate(endDate));
    }, [onFilterChange]);

    /**
     * 현재 선택된 프리셋 확인
     */
    const activePreset = useMemo((): DatePreset | null => {
        if (!filters.acctStDate || !filters.acctEdDate) return null;

        const today = new Date();
        const dayOfWeek = today.getDay();
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;

        // 이번 주
        const thisWeekStart = new Date(today);
        thisWeekStart.setDate(today.getDate() + diffToMonday);
        const thisWeekEnd = new Date(thisWeekStart);
        thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
        if (filters.acctStDate === formatDate(thisWeekStart) && filters.acctEdDate === formatDate(thisWeekEnd)) {
            return 'thisWeek';
        }

        // 지난 주
        const lastWeekStart = new Date(today);
        lastWeekStart.setDate(today.getDate() + diffToMonday - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        if (filters.acctStDate === formatDate(lastWeekStart) && filters.acctEdDate === formatDate(lastWeekEnd)) {
            return 'lastWeek';
        }

        // 이번 달
        const thisMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const thisMonthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        if (filters.acctStDate === formatDate(thisMonthStart) && filters.acctEdDate === formatDate(thisMonthEnd)) {
            return 'thisMonth';
        }

        // 지난 달
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        if (filters.acctStDate === formatDate(lastMonthStart) && filters.acctEdDate === formatDate(lastMonthEnd)) {
            return 'lastMonth';
        }

        return null;
    }, [filters.acctStDate, filters.acctEdDate]);

    // 협력사 옵션을 SelectOption 형식으로 변환
    const supplierOptions: SelectOption[] = useMemo(() => {
        return suppliers.map((supplier) => ({
            value: supplier.id,
            label: supplier.name
        }));
    }, [suppliers]);

    // 사용자 타입에 따라 그리드 클래스 결정
    const getFormClassName = () => {
        if (isSupplierUser) {
            return 'search-form rider-pre-settlement-search-form-supplier';
        }
        return 'search-form rider-pre-settlement-search-form-hq';
    };

    return (
        <div className="card search-card rider-pre-settlement-filter">
            <div className="card-body">
                <div className={getFormClassName()}>
                    {/* 조회일자 */}
                    <div className="form-group date-range-group">
                        <div className="form-field">
                            <div className="form-label-row">
                                <label className="form-label">
                                    <i className="fas fa-calendar-alt" />
                                    조회일자
                                </label>
                                {/* 기간 프리셋 버튼 */}
                                <div className="date-preset-buttons">
                                    <button
                                        type="button"
                                        className={`date-preset-btn ${activePreset === 'thisWeek' ? 'active' : ''}`}
                                        onClick={() => handlePresetClick('thisWeek')}
                                    >
                                        이번 주
                                    </button>
                                    <button
                                        type="button"
                                        className={`date-preset-btn ${activePreset === 'lastWeek' ? 'active' : ''}`}
                                        onClick={() => handlePresetClick('lastWeek')}
                                    >
                                        지난 주
                                    </button>
                                    <button
                                        type="button"
                                        className={`date-preset-btn ${activePreset === 'thisMonth' ? 'active' : ''}`}
                                        onClick={() => handlePresetClick('thisMonth')}
                                    >
                                        이번 달
                                    </button>
                                    <button
                                        type="button"
                                        className={`date-preset-btn ${activePreset === 'lastMonth' ? 'active' : ''}`}
                                        onClick={() => handlePresetClick('lastMonth')}
                                    >
                                        지난 달
                                    </button>
                                </div>
                            </div>
                            <div className="date-range-inputs">
                                <DateInput
                                    id="filterAcctStDate"
                                    value={filters.acctStDate}
                                    onChange={(value) => onFilterChange('acctStDate', value)}
                                    onKeyPress={handleKeyPress}
                                />
                                <span className="date-separator">~</span>
                                <DateInput
                                    id="filterAcctEdDate"
                                    value={filters.acctEdDate}
                                    onChange={(value) => onFilterChange('acctEdDate', value)}
                                    onKeyPress={handleKeyPress}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 라이더명 */}
                    <div className="form-group">
                        <div className="form-field">
                            <label className="form-label">
                                <i className="fas fa-user" />
                                라이더명
                            </label>
                            <input
                                type="text"
                                className="text-input"
                                value={filters.riderName}
                                onChange={(e) => onFilterChange('riderName', e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="라이더명 입력..."
                            />
                        </div>
                    </div>

                    {/* 협력사 */}
                    {!isSupplierUser && (
                        <div className="form-group">
                            <div className="form-field">
                                <label className="form-label">
                                    <i className="fas fa-building" />
                                    협력사
                                </label>
                                <SearchableSelect
                                    options={supplierOptions}
                                    value={filters.supplierId}
                                    onChange={(value) => onFilterChange('supplierId', value)}
                                    placeholder="협력사 검색..."
                                    showAllOption={true}
                                    allOptionLabel="전체"
                                />
                            </div>
                        </div>
                    )}

                    {/* 버튼 그룹 */}
                    <div className="form-group button-group">
                        {!isSupplierUser && (
                            <button className="btn btn-success" onClick={onExcelDownload}>
                                <i className="fas fa-file-excel" />
                                엑셀
                            </button>
                        )}
                        <button className="btn btn-primary" onClick={onSearch}>
                            <i className="fas fa-search" />
                            조회
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FilterSection;
